import {
  AllowedFileTypes,
  AllowedImageFileTypes,
  AllowedPDFFileTypes,
  FileTypeToExtensionMapping
} from "./FileTypeDetails";

const PAN_PATTERN = "^[a-zA-Z]{3}[CPHFATBLJGcphfatbljg]{1}[a-zA-Z]{1}[0-9]{4}[a-zA-Z]{1}$";
const PERSON_NAME_PATTERN = "^[a-zA-Z\\s]+$";
const POSTAL_CODE_PATTERN = "^[0-9]{6}$";
const IFSC_PATTERN = "^[A-Za-z]{4}0[A-Z0-9a-z]{6}$";
const PHONE_NUMBER_PATTERN = "(^[6-9][0-9]{9}$)";
const YEARS_PATTERN = "^[0-9]{1,2}$";
const MONTHS_PATTERN = "(^0$|^0?[0-9]$|^1[01]$)";
const NUMBER_PATTERN = "(^[0-9]+$)";
const NUMBER_WITH_OPTIONAL_DECIMAL_PATTERN = "(^[0-9]+[.]*[0-9]+$|^[0-9]+$)";

import { Constants } from "@/components/Constants";
import DateUtils, { DateFormats } from "./DateUtils";
import {ValidationPatterns} from "@/utils/ValidationPatterns";

export default {

  convertToJSONObjectIfString(someObject) {
    if (this.shouldParseUsingJSON(someObject)) {
      try {
        return JSON.parse(this.removeNonValidJSONCharIfString(someObject));
      } catch (e) {
        return {};
      }
    }

    return someObject;
  },

  removeNonValidJSONCharIfString(someObject) {
    if (typeof someObject === "string") {
      // preserve newlines, etc - use valid JSON
      someObject = someObject.replace(/\\n/g, "\\n")
        .replace(/\\'/g, "\\'")
        .replace(/\\"/g, '\\"')
        .replace(/\\&/g, "\\&")
        .replace(/\\r/g, "\\r")
        .replace(/\\t/g, "\\t")
        .replace(/\\b/g, "\\b")
        .replace(/\\f/g, "\\f");
      // remove non-printable and other non-valid JSON chars
      someObject = someObject.replace(/[\u0000-\u0019]+/g, "");
    }
    return someObject;
  },

  shouldParseUsingJSON(someObject) {
    return this.areEqualString(typeof someObject, "string", true)
  },

  isObjectString(object) {
    return this.areEqualString("string", typeof object, true);
  },

  isObjectBoolean(object) {
    return this.areEqualString("boolean", typeof object, true);
  },

  areEqualString(str1, str2, ignoreCase) {
    if (str1 === str2) {
      return true;
    }

    if (str1 == null || str2 == null) {
      return false;
    }
    if (ignoreCase) {
      return str1.toUpperCase() === str2.toUpperCase();
    } else {
      return str1 === str2;
    }
  },

  isEmpty(strOrArray) {
    return !strOrArray || strOrArray.length === 0;
  },

  isNotEmpty(strOrArray) {
    return !this.isEmpty(strOrArray);
  },

  isValidString(str) {
    return this.areEqualString("string", typeof str, true)
      && this.isNotEmpty(str)
      && !this.areEqualString("undefined", str, true)
      && !this.areEqualString("null", str, true)
  },

  isValidNumber(objct) {
    return this.areEqualString("number", typeof objct, true)
  },

  isAValidNumber(object) {
    if (object == null) {
      return false;
    }
    object = object.toString();
    return this.doesPatternMatch(NUMBER_WITH_OPTIONAL_DECIMAL_PATTERN, object);
  },

  isBoolean(objct) {
    return this.areEqualString("boolean", typeof objct, true)
  },

  isValidId(id) {
    return id > 0;
  },

  isTrue(boolean) {
    if (boolean) {
      if (this.isObjectString(boolean)) {
        return this.areEqualString("true", boolean, true);
      }
      return this.isObjectBoolean(boolean);
    }

    return false;
  },

  isFalse(boolean) {
    return !this.isTrue(boolean);
  },

  convertToNumber(numberObject, defaultValue) {
    defaultValue = this.isValidNumber(defaultValue) ? defaultValue : null;

    if (numberObject || this.isValidNumber(numberObject)) {
      numberObject = numberObject.toString();

      const convertedNumber = Number(numberObject);

      if (!this.areEqualString(convertedNumber.toString(), "Nan", true)) {
        return convertedNumber;
      }
    }

    return defaultValue;
  },

  convertToUpperCamelCase(fullString, removeWhiteSpaceAtEnd) {
    let returnValue = ""
    if (fullString) {
      let camelCaseArray = []
      const splitArrayString = fullString.toString().split(/[ ]+/)
      splitArrayString && splitArrayString.forEach(str => {
        if (this.isValidString(str) && this.isNotEmpty(str.trim())) {
          str = str.trim();
          camelCaseArray.push(str.substring(0, 1).toUpperCase() + str.substring(1).toLowerCase())
        }
      })
      returnValue = this.joinArray(" ", camelCaseArray)
      if (!removeWhiteSpaceAtEnd && ' ' === fullString.charAt(fullString.toString().length - 1)) {
        returnValue += ' '
      }
    }

    return returnValue
  },

  mergeName(firstName, middleName, lastName) {
    let fullName = "";
    if (this.isValidString(firstName)) {
      fullName = firstName;
    }
    if (this.isValidString(middleName)) {
      fullName = fullName + " " + middleName;
    }
    if (this.isValidString(lastName)) {
      fullName = fullName + " " + lastName;
    }
    return fullName;
  },

  parseFullName(fullName) {
    let parsedName = ["", "", ""];
    if (this.isValidString(fullName)) {
      let splitName = fullName.split(" ");
      if (splitName.length === 1) {
        parsedName[0] = splitName[0]
      } else if (splitName.length === 2) {
        parsedName[0] = splitName[0];
        parsedName[2] = splitName[1];
      } else {
        parsedName[0] = splitName[0];
        parsedName[1] = this.mergeStringArray(" ", splitName.slice(1, splitName.length - 1));
        parsedName[2] = splitName[splitName.length - 1];
      }
    }
    return parsedName;
  },

  mergeStringArray(delimiter, stringArray) {
    let result = "";
    let firstTime = true;
    if (stringArray) {
      for (let i = 0; i < stringArray.length; i++) {
        if (this.isValidString(stringArray[i])) {
          if (firstTime) {
            firstTime = false;
            result = stringArray[i];
          } else {
            result = result + delimiter + stringArray[i];
          }
        }
      }
    }
    return result;
  },

  joinArray(delimiter, stringTokens) {
    let result = "";
    let firstTime = true;
    if (stringTokens) {
      for (let index in  stringTokens) {
        let token = stringTokens[index]
        if (token != null) {
          token = token.toString();
        }
        if (this.isNotEmpty(token)) {
          if (firstTime) {
            firstTime = false;
            result = token;
          } else {
            result = result + delimiter + token;
          }
        }
      }
    }
    return result;
  },

  join(delimiter, ...stringTokens) {
    let result = "";
    let firstTime = true;
    if (stringTokens) {
      for (let index in  stringTokens) {
        let token = stringTokens[index]
        if (token != null) {
          token = token.toString();
        }
        if (this.isNotEmpty(token)) {
          if (firstTime) {
            firstTime = false;
            result = token;
          } else {
            result = result + delimiter + token;
          }
        }
      }
    }
    return result;
  },

  filterLoanActionsByStatus(loanTasks, states) {
    let filteredTasks = [];
    loanTasks && loanTasks.forEach(loanTask => {
      if (loanTask.task_status != null && states.includes(loanTask.task_status)) {
        filteredTasks.push(loanTask)
      }
    });
    return filteredTasks;
  },

  getNonRepeatableLoanAction(allTasks) {
    let allLoanActions = [];
    allTasks && allTasks.forEach(task => {
      if (task && task.task_completion_type !== 'REPEATABLE') {
        allLoanActions.push(task)
      }
    });
    return allLoanActions;
  },

  getRepeatableLoanAction(allTasks) {
    let allLoanActions = [];
    allTasks && allTasks.forEach(task => {
      if (task && task.task_completion_type === 'REPEATABLE') {
        allLoanActions.push(task)
      }
    });
    return allLoanActions;
  },

  /***
   * this method is used to check if value contains pattern or not
   * @param pattern regex to check
   * @param value against
   * @returns {boolean}
   */
  doesPatternMatch(pattern, value) {
    return new RegExp(pattern).test(value);
  },

  validatePan(panNumber) {
    return this.doesPatternMatch(PAN_PATTERN, panNumber)
  },

  validatePersonName(personName) {
    return this.isValidString(personName)
      && this.doesPatternMatch(PERSON_NAME_PATTERN, personName.trim());
  },

  validatePostalCode(postalCode) {
    return this.doesPatternMatch(POSTAL_CODE_PATTERN, postalCode)
  },

  validateIFSC(ifscCode) {
    return this.doesPatternMatch(IFSC_PATTERN, ifscCode)
  },

  validatePhoneNumber(phoneNumber) {
    return phoneNumber != null && this.doesPatternMatch(PHONE_NUMBER_PATTERN, phoneNumber)
  },

  validateYear(years) {
    if (years == null) {
      return false;
    }
    years = years.toString();
    return this.doesPatternMatch(YEARS_PATTERN, years)
  },

  validateMonths(months) {
    if (months == null) {
      return false;
    }
    months = months.toString();
    return this.doesPatternMatch(MONTHS_PATTERN, months)
  },

  validateEmail(email) {
    return email != null && this.doesPatternMatch(ValidationPatterns.EMAIL_PATTERN, email);
  },

  containsOnlyNumbers(object) {
    if (object == null) {
      return false;
    }
    object = object.toString();
    return this.doesPatternMatch(NUMBER_PATTERN, object);
  },

  removeUnderscoreFromString(inputString, excludedWords) {
    let returnValue = "";
    if (!inputString) {
      returnValue = ""
    } else {
      inputString = inputString.toString();
      let splitWords = inputString.split('_')
      let words = []
      splitWords && splitWords.forEach(word => {
        if (this.isNotEmpty(word)) {
          if (excludedWords && excludedWords.includes(word)) {
            words.push(word)
          } else {
            words.push(word.substring(0, 1).toUpperCase() + word.substring(1).toLowerCase())
          }
        }
      })
      returnValue = this.joinArray(" ", words)
    }
    return returnValue;
  },

  removeCharsAndJoinWithUpperCase(string, ...chars) {
    if (!string) {
      return "";
    }

    const regexString = this.joinArray("|", chars);
    const words = string.split(new RegExp(regexString));
    for (let i = 0, n = words.length; i < n; i++) {
      if (words[i]) {
        let word = words[i].toLowerCase();
        words[i] = word.substring(0, 1).toUpperCase() + word.substring(1);
      }
    }

    return this.joinArray(" ", words);
  },

  getFileNameWithTimestamp(file, fileNameMaxLength) {
    fileNameMaxLength = fileNameMaxLength ? fileNameMaxLength : 45;
    if (file) {
      const fileExtension = FileTypeToExtensionMapping[file.type]
        ? FileTypeToExtensionMapping[file.type]
        : "";
      const fileNameWithExtension = file.name;
      const fileNameWithoutExtension = fileNameWithExtension.replace(`.${fileExtension}`, "");

      const fileNameWithTimestamp = `${fileNameWithoutExtension}_${DateUtils.getCurrentTimeAsFormat(
        DateFormats.YYYYMMDD_HHmmss_SSS)}`.substr(-fileNameMaxLength, fileNameMaxLength);
      return fileExtension ? `${fileNameWithTimestamp}.${fileExtension}` : fileNameWithTimestamp;
    }
    return "";
  },

  isFilePDF(file) {
    return file && AllowedPDFFileTypes.includes(file.type);
  },

  isFileImage(file) {
    return file && AllowedImageFileTypes.includes(file.type);
  },

  isFileAllowedToUpload(file) {
    return file && AllowedFileTypes.includes(file.type);
  },

  toCamelCase(someString) {
    if (!someString) {
      return ''
    }
    const words = someString.toString().split(" ")
    let capitalizedWords = []
    words.forEach(word => {
      if (word && this.isNotEmpty(word.trim())) {
        word = word.trim()
        word = word.substring(0, 1).toUpperCase() + word.substring(1).toLowerCase()
        capitalizedWords.push(word)
      }
    })
    return this.joinArray(" ", capitalizedWords)
  },

  isLoanRejected(ezLoan) {
    return this.areEqualString(Constants.REJECTED, this.getLoanStatus(ezLoan), true);
  },

  getLoanStatus(ezLoan) {
    if (ezLoan && ezLoan.loan && ezLoan.loan.status
      && ezLoan.loan.status.value) {
      return ezLoan.loan.status.value;
    }
    return ''
  },

  getParamFromUrl(key, url) {
    if (!key || !url) {
      return null
    }
    const reg = new RegExp('[?|&]' + key + '=([^&]+)');
    const match = url.match(reg);
    return match && match[1];
  },

  getHumanReadableAddress(address) {
    return !address ? "" : this.join(", ", address.street, address.addressLine1,
      address.addressLine2, address.addressLine3, address.city, address.stateName,
      address.postalCode
    );
  },

  appendQueryParam(query, queryKey, queryParam) {
    query = query ? `${query}&` : `?`;
    return `${query}${queryKey}=${queryParam}`;
  },

  getFileFromBaseSixtyFour(base64) {
    var binary = this.fixBinary(atob(base64));
    const blob = new Blob([binary], {type: 'image/jpeg'});
    const file = new File([blob], "identifier.jpg",{ type: "image/jpeg" })
    return file;
  },

  fixBinary(bin) {
    var length = bin.length;
    var buf = new ArrayBuffer(length);
    var arr = new Uint8Array(buf);
    for (var i = 0; i < length; i++) {
      arr[i] = bin.charCodeAt(i);
    }
    return buf;
  },

  timeDifferenceInMinute(dt2, dt1){
   let diff =(dt2.getTime() - dt1.getTime()) / 1000;
   diff /= 60;
   return Math.abs(Math.round(diff));
  },

  getLastNameAfter(name, separator, lable){
    if(lable && name && separator){
      let nameSeparator = name+separator;
      let lableArray = lable.split(nameSeparator);
      return lableArray[lableArray.length-1];
    }else{
      return lable;
    }
  },

  getColumnNameWithoutCodeLookup(columnHeader) {
    let columnName = null;

    if (columnHeader) {
      columnName = columnHeader.columnName || columnHeader.dataTableColumnName;
      if (this.areEqualString(
          Constants.CODELOOKUP, columnHeader.columnDisplayType, true
          )
          && columnName
          && columnHeader.columnCode
      ) {

        columnName = columnName.substring(
            columnName.indexOf(Constants.CODE_VALUE_SEPARATOR) + 4
        );
      }
    }

    return columnName;
  },

  getFieldValueForColumnHeader(columnHeader, jsonElement) {
    let value = null;
    if (columnHeader && jsonElement) {
      const columnName =
          columnHeader.columnName || columnHeader.dataTableColumnName;

      const columnValueElement =
          this.getFieldValueForColumnName(columnName, jsonElement);
      value = columnValueElement;

      if (this.areEqualString(
          Constants.CODELOOKUP, columnHeader.columnDisplayType, true
      )) {
        value = this.getFieldValueForCodeLookupColumn(
            columnValueElement, columnHeader
        );
      }
    }

    if (typeof value === "boolean" || typeof value === "number") {
      value = value.toString();
    }

    return value ? value.toString() : value;
  },

  getFieldValueForColumnName(columnName, jsonElement) {
    return jsonElement && jsonElement[columnName]
        ? jsonElement[columnName] : null;
  },

  getFieldValueForCodeLookupColumn(columnValueId, columnHeader) {
    let columnValueString = null;
    if (columnHeader) {
      const columnValues = columnHeader.columnValues;

      columnValues && columnValues.forEach(columnValue => {
        if (columnValue.id === columnValueId) {
          columnValueString = columnValue.value;
        }
      })
    }

    return columnValueString;
  },

  getColumnHeaderForColumnName(dataTableTemplate, columnName) {
    let columnHeader = null;

    if (columnName && dataTableTemplate) {
      const columnHeadersData = dataTableTemplate["columnHeaderData"];
      columnHeadersData && columnHeadersData.forEach(columnHeaderData => {
        if (columnName === this.getColumnNameWithoutCodeLookup(columnHeaderData)
        ) {
          columnHeader = columnHeaderData;
        }
      });
    }

    return columnHeader;
  },

  isFieldNested(fieldName) {
    return fieldName && fieldName.includes(Constants.FIELD_CHILD_SEPARATOR);
  },

  getFieldValueForChildObject(field, jsonElement) {
    if (field && jsonElement) {
      const splitChildNames = field.split(Constants.FIELD_CHILD_SEPARATOR);
      const jsonObject = this.convertToJSONObjectIfString(jsonElement);
      if (jsonObject && jsonObject[splitChildNames[0]]) {
        return this.getChildValue(
            jsonObject[splitChildNames[0]],
            1,
            splitChildNames
        );
      }
    }
    return null;
  },

  getChildValue(mapOrValue, childIndex, splitChildNames) {
    if (!mapOrValue || !splitChildNames) {
      return null;
    }

    if (childIndex === splitChildNames.length) {
      return mapOrValue.toString();
    }

    const convertedMap = this.convertToJSONObjectIfString(mapOrValue);
    if (!convertedMap) {
      return null;
    }

    return this.getChildValue(
        convertedMap[splitChildNames[childIndex]],
        ++childIndex,
        splitChildNames
    );
  },

  getParentFieldName(fieldName) {
    if (!fieldName) {
      return "";
    }

    return fieldName.split(Constants.FIELD_CHILD_SEPARATOR)[0];
  },

  setNestedJsonFields(fieldFullName, childIndex, parentMap, fieldValue) {
    if (!fieldFullName) {
      return parentMap;
    }
    const splitChildNames =
        fieldFullName.split(Constants.FIELD_CHILD_SEPARATOR);

    const fieldName = splitChildNames[childIndex];
    if (!parentMap) {
      parentMap = {};
    }

    if (childIndex === splitChildNames.length - 1) {
      parentMap[fieldName] = fieldValue;
    } else {
      let mapToPass = {};
      if (typeof parentMap[fieldName] === "object") {
        mapToPass = parentMap[fieldName];
      }
      parentMap[fieldName] = this.setNestedJsonFields(
          fieldFullName, ++childIndex, mapToPass, fieldValue
      );
    }

    return parentMap;
  },

  getChildFieldName(fieldName) {
    if (!fieldName) {
      return "";
    }
    const splitChildNames = fieldName.split(Constants.FIELD_CHILD_SEPARATOR);
    return splitChildNames[splitChildNames.length - 1];
  },

  getStageFieldDataForColumnName(stageFieldDataList, columnName) {
    let stageFieldData = null;
    if (columnName && this.isNotEmpty(stageFieldDataList)) {
      for (let i = 0, n = stageFieldDataList.length; i < n; i++) {
        const stageFieldData1 = stageFieldDataList[i];
        if (stageFieldData1["field"] === columnName) {
          stageFieldData = stageFieldData1;
          break;
        }
      }
    }

    return stageFieldData;
  },

  convertChildFieldsToNormalField(
      parentFieldName,
      stageFieldData,
      dependsOnField,
      dependsOnFieldValues
  ) {
    const stageFieldDataList = [];
    const fieldName = parentFieldName
        ? this.join(
            Constants.FIELD_CHILD_SEPARATOR,
            parentFieldName,
            stageFieldData["field"]
        )
        : stageFieldData["field"];

    const childObject = stageFieldData["childObject"];
    if (this.isEmpty(childObject)) {
      stageFieldData["field"] = fieldName;

      if (this.isEmpty(stageFieldData["dependsOnField"])
          && this.isEmpty(stageFieldData["dependsOnFieldValues"])
      ) {
        stageFieldData["dependsOnField"] = dependsOnField;
        stageFieldData["dependsOnFieldValues"] = dependsOnFieldValues;
      }

      stageFieldDataList.push(stageFieldData);
    } else {
      childObject.forEach(childStageFieldData => {
        if (this.isEmpty(dependsOnField)
            && this.isEmpty(dependsOnFieldValues)
        ) {
          dependsOnField = childStageFieldData["dependsOnField"];
          dependsOnFieldValues = childStageFieldData["dependsOnFieldValues"];
        }

        childStageFieldData["childFieldCreatedManually"] = true;
        stageFieldDataList.push(...this.convertChildFieldsToNormalField(
            fieldName,
            childStageFieldData,
            dependsOnField,
            dependsOnFieldValues
        ));
      });
    }

    return stageFieldDataList;
  },

  getValueFromObject(object, key, defaultValue) {
    let value = defaultValue;
    if (object
        && key
        && (typeof object[key] === "boolean"
            || typeof object[key] === "number"
            || object[key]
        )
    ) {
      value = object[key];
    }

    return value;
  }
};
